import React, { useEffect, useState } from "react";
import { Button, Table, Tag, Modal, Select, Tooltip } from "antd";
const { Column, ColumnGroup } = Table;
import CardDiv from "modules/common/card-div/CardDiv";
import { useAttendancePageState, useLeaveBalancePageState } from "./store";
import { useToast } from "modules/navbar/top/store/toasterStore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// import { LuPencil } from "react-icons/lu";
import { Input } from 'antd';
const { TextArea } = Input;
import { Pagination } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { attendanceDownload } from "./api";
import { approvalSelectData, leaveDetailsAbbreviation, leaveDetailsAbbreviationOutsource, leaveDetailsColor } from "./constants";
import { isDownloadDisabled } from "modules/utilities/utility";

const AttendanceTracker = () => {

    // Using Toast to show notification
    const { setToaster } = useToast();

    // Defining State
    const [selectedDate, setSelectedDate] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [userHouse, setUserHouse] = useState('In-house');

    // Importing required zustand State
    const { isLoading, setIsLoading, attendanceData, fetchAttendanceFunc, updateUserFunc, isEditModalOpen, setEditModalToggle, selectedItem, updateSelectedItem, selectedFilter, updateSelectedFilter, uniqueTeams, uniqueManagers, uniqueHouse } = useAttendancePageState();

    // Importing required zustand State
    const { leaveData } = useLeaveBalancePageState();

    // Transform data into an object with dates as keys and attendance status as values
    const transformedData = attendanceData?.usersDetail?.map((item) => {
        const attendanceObject = {};
        item.dateMap.forEach(({ loginDate, status }) => {
            loginDate = new Date(loginDate).getDate();
            attendanceObject[loginDate] = status;
        });
        attendanceObject["sno"] = item.userId;
        return { ...item, ...attendanceObject };
    });

    const leaveTypeOptions = [{ value: "0", label: "Select Leave type" }, ...leaveData?.leaveDetails?.map(leave => ({ value: leave.leaveId, label: leave.leaveName }))];

    useEffect(() => {
        // Fetching initial data including house options from backend...
        fetchAttendanceBaseFunc({ isFirstTime: true, house: userHouse });
    }, [userHouse]);

    const onChangeDate = (date) => {
        setSelectedDate(date);
        // Calling our api to get data from backend...
        updateSelectedFilter("month", date.getMonth() + 1);
        updateSelectedFilter("year", date.getFullYear());
        fetchAttendanceBaseFunc({ month: date.getMonth() + 1, year: date.getFullYear(), house: userHouse });
    };

    const handleChangeSelectedItem = (name, value) => {
        updateSelectedItem(name, value);
    };

    const handleChange = (name, value) => {
        updateSelectedFilter(name, value);

        // Calling our data from backend...
        if (name === "team") {
            fetchAttendanceBaseFunc({ team: value, house: userHouse });
        } else if (name === "reportingManagerIdsArray") {
            fetchAttendanceBaseFunc({ reportingManagerIdsArray: value, house: userHouse });
        } else if (name === "house") {
            setUserHouse(value);
        } else {
            fetchAttendanceBaseFunc({ house: userHouse });
        }
    };

    const handlePageChange = (page, size) => {
        // Updating State
        updateSelectedFilter("pageNumber", page);
        updateSelectedFilter("pageSize", size);
        // Calling our data from backend...
        fetchAttendanceBaseFunc({ pageNumber: page, pageSize: size, house: userHouse });
    }

    const handleManualDownloadClick = async () => {
        setIsDownloading(true);
        try {
            // Fetch the attendance data based on the provided filters
            const response = await attendanceDownload({
                reportingManagerIdsArray: selectedFilter.reportingManagerIdsArray,
                team: selectedFilter.team,
                month: selectedFilter.month,
                year: selectedFilter.year,
                isDownloadReq: true,
                house: userHouse
            });

            // Assume response.data contains the CSV data
            const csvData = response.data;

            // Default filename if none is provided in the response headers
            let filename = 'attendance.csv';

            // Check if the response headers contain a 'Content-Disposition' header with the filename
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                // Extract the filename from the 'Content-Disposition' header using a regular expression
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch.length > 1) {
                    filename = filenameMatch[1]; // Use the extracted filename if available
                }
            }
            // Create a Blob from the CSV data with the appropriate MIME type
            const blob = new Blob([csvData], { type: 'text/csv' });
            // Create a temporary URL for the Blob
            const url = URL.createObjectURL(blob);
            // Create a temporary anchor element to initiate the download
            const a = document.createElement('a');
            a.href = url;
            a.download = filename; // Set the filename for the downloaded file
            // Append the anchor to the document body and trigger a click to start the download
            document.body.appendChild(a);
            a.click();
            // Remove the anchor from the document after the download is initiated
            document.body.removeChild(a);
            // Revoke the temporary URL to free up memory
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading CSV file:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    function fetchAttendanceBaseFunc(overrides = {}) {
        const {
            month = selectedFilter.month,
            year = selectedFilter.year,
            team = selectedFilter.team,
            reportingManagerIdsArray = selectedFilter.reportingManagerIdsArray,
            isFirstTime = false,
            pageSize = selectedFilter.pageSize,
            pageNumber = selectedFilter.pageNumber,
            house = userHouse,
        } = overrides;

        fetchAttendanceFunc(setToaster, { month, year, team, reportingManagerIdsArray, pageSize, isFirstTime, pageNumber, house });
    }

    return (
        <>
            <CardDiv
                title={<>
                    <div className="d-flex align-items-center justify-content-between">
                        <h3 className="fw-bold">Attendance Tracker</h3>
                        <Button type="primary" icon={<DownloadOutlined />} loading={isDownloading} onClick={handleManualDownloadClick} disabled={isDownloadDisabled && isDownloading}>
                            Download Excel
                        </Button>
                    </div>
                </>}
                component={
                    <>
                        <div className="d-flex flex-column gap-2 align-items-center justify-content-center mb-5">
                            <h4>Abbreviations</h4>
                            <div className="w-75 d-flex gap-3 flex-wrap align-items-center justify-content-center">
                                {userHouse === 'Outsource' 
                                ?
                                Object.entries(leaveDetailsAbbreviationOutsource)?.map(([key, value]) => (
                                    <Tag color={leaveDetailsColor[key]} className="fw-bold" key={key}>
                                        {key} : {value}
                                    </Tag>
                                ))
                                :
                                Object.entries(leaveDetailsAbbreviation)?.map(([key, value]) => (
                                    <Tag color={leaveDetailsColor[key]} className="fw-bold" key={key}>
                                        {key} : {value}
                                    </Tag>
                                ))
                                }
                                
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between my-3 mx-5">
                            <div className="fw-bolder"> Total Count: {attendanceData?.totalCount}</div>
                            <div className="d-flex align-items-center justify-content-center gap-2">
                                <Select
                                    placeholder="Select House"
                                    style={{ width: '200px' }}
                                    value={userHouse}
                                    onChange={value => handleChange("house", value)}
                                    options={uniqueHouse}
                                />
                                <Select
                                    placeholder="Select Team"
                                    style={{ width: '200px' }}
                                    onChange={value => handleChange("team", value)}
                                    options={uniqueTeams}
                                />
                                <Select
                                    placeholder="Select Reporting Manager"
                                    mode={ "multiple"}
                                    style={{ width: '200px' }}
                                    onChange={value => handleChange("reportingManagerIdsArray", value)}
                                    options={uniqueManagers}
                                    showSearch 
                                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                />
                                <span>
                                    <DatePicker showIcon selected={selectedDate} onChange={onChangeDate} dateFormat="MM/yyyy"
                                        showMonthYearPicker
                                        placeholderText="Select Month"
                                        className="px-4 py-2 react-calender-input"
                                        calendarIconClassname="mt-1"
                                    />
                                </span>
                            </div>
                        </div>
                        <Table dataSource={transformedData} scroll={{ x: 5000, y: 600 }} pagination={false}>

                            <Column title="Name" dataIndex={"userName"} key={"userName"} fixed="left" />
                            <Column title="Reporting Manager" dataIndex={"reportingManagerName"} key={"reportingManagerName"} fixed="left" />
                            <Column title="Team" dataIndex={"team"} key={"team"} fixed="left" />

                            <ColumnGroup title="Head Count" >
                                <ColumnGroup title="Planned"  >
                                    <ColumnGroup title="Unplanned"  >
                                        <ColumnGroup title="Shrinkage">
                                            <Column title="Dates :" fixed="left" />
                                        </ColumnGroup>
                                    </ColumnGroup>
                                </ColumnGroup>
                            </ColumnGroup>

                            {attendanceData?.headCount?.map((count, index) => (
                                <ColumnGroup title={count} key={index}>
                                    <ColumnGroup title={attendanceData.plannedLeaves[index]} >
                                        <ColumnGroup title={attendanceData.unplannedLeaves[index]} >
                                            <ColumnGroup title={attendanceData.shrinkagePercentage[index]}>
                                                <Column title={index + 1} dataIndex={index + 1} key={index + 1}
                                                    render={(tag) => {
                                                        return <Tag color={leaveDetailsColor[tag] || "blue"} className="fw-bold"> {tag} </Tag>
                                                    }}
                                                />
                                            </ColumnGroup>
                                        </ColumnGroup>
                                    </ColumnGroup>
                                </ColumnGroup>
                            ))}
                        </Table>

                        {/* Pagination Functionality Added */}
                        <div className="d-flex align-items-center justify-content-center my-3">
                            <Pagination defaultCurrent={1} pageSize={selectedFilter.pageSize} total={attendanceData?.totalCount} onChange={handlePageChange}
                                onShowSizeChange={(current, size) => handlePageChange(current, size)} />
                        </div>

                        <Modal title="Update Attendance" open={isEditModalOpen} onOk={setEditModalToggle} onCancel={setEditModalToggle}
                            footer={[
                                <Button key="cancel" onClick={setEditModalToggle}> Cancel </Button>,
                                <Button key="submit" type="primary" loading={isLoading} onClick={() => { updateUserFunc(selectedItem.comments, isLeaveApproved, setToaster) }}> Apply </Button>
                            ]}>

                            <div className="d-flex gap-5 py-5">
                                <Select
                                    className="w-100"
                                    options={approvalSelectData}
                                    value={selectedItem.approval}
                                    onChange={(value) => handleChangeSelectedItem('approval', value)}
                                />

                                <Select
                                    className="w-100"
                                    value={selectedItem.leaveType}
                                    onChange={(value) => handleChangeSelectedItem('leaveType', value)}
                                    options={leaveTypeOptions}
                                />
                            </div>

                            <div className="d-flex flex-column gap-5">

                                <Input type="date" value={selectedItem.date} onChange={(e) => handleChangeSelectedItem("date", e.target.value)} />

                                <TextArea placeholder="Comments" autoSize={{ minRows: 2, maxRows: 6 }} value={selectedItem.comments} onChange={(e) => handleChangeSelectedItem("comments", e.target.value)} />

                            </div>

                        </Modal>
                    </>
                }
            />
        </>
    );
};

export default AttendanceTracker;
